import React, { useState, useEffect } from 'react';

const ImageModal = ({ room, onClose }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  if (!room) return null;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev === room.images.length - 1 ? 0 : prev + 1));
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev === 0 ? room.images.length - 1 : prev - 1));
  };

  // Get features data from room object
  const getFeaturesData = () => {
    // Your API now returns "features" not "amenities"
    if (room.features && typeof room.features === 'object' && Object.keys(room.features).length > 0) {
      return room.features;
    }
    
    // Fallback for old structure (if any)
    if (room.amenities && Array.isArray(room.amenities) && room.amenities.length > 0) {
      return {
        "Room Amenities": room.amenities
      };
    }
    
    return {};
  };

  const features = getFeaturesData();

  // Define the order of feature categories
  const categoryOrder = [
    "Beds and Blankets",
    "Room Amenities", 
    "Room Features",
    "Bathroom",
    "Safety"
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm p-4 md:p-10">
      
      {/* WRAPPER */}
      <div className="relative w-full max-w-6xl group">
        
        {/* DESKTOP NAVIGATION ARROWS */}
        {room.images.length > 1 && (
          <div className="hidden md:block">
            <button onClick={prevImage} className="absolute top-1/2 -left-16 -translate-y-1/2 p-3 text-white/50 hover:text-white transition-all z-50">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-10 h-10">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            <button onClick={nextImage} className="absolute top-1/2 -right-16 -translate-y-1/2 p-3 text-white/50 hover:text-white transition-all z-50">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-10 h-10">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>
        )}

        {/* MAIN MODAL BOX */}
        <div className="bg-white rounded-xl shadow-2xl w-full h-full max-h-[90vh] md:h-[390px] overflow-hidden relative flex flex-col md:flex-row">
          
          <button onClick={onClose} className="absolute top-4 right-4 z-50 p-2 bg-black/20 hover:bg-black/50 rounded-full text-white backdrop-blur-md transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* LEFT: IMAGE SECTION (60%) */}
          <div className="relative w-full md:w-[60%] bg-black overflow-hidden flex-shrink-0">
            <div className="relative w-full h-full z-10">
              {room.images.map((image, index) => (
                <img
                  key={index}
                  src={image}
                  alt={room.category_name || room.name}
                  className={`w-full h-full object-cover transition-opacity duration-500 
                    ${index === currentImageIndex ? 'opacity-100' : 'opacity-0 absolute'}
                  `}
                />
              ))}
            </div>

            {/* MOBILE NAVIGATION ARROWS */}
            {room.images.length > 1 && (
              <div className="md:hidden absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-4 z-20">
                <button onClick={prevImage} className="p-2 bg-black/40 rounded-full text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                  </svg>
                </button>
                <button onClick={nextImage} className="p-2 bg-black/40 rounded-full text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                  </svg>
                </button>
              </div>
            )}

            {/* PAGINATION DOTS */}
            {room.images.length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-1 rounded-full transition-all duration-300 ${
                      index === currentImageIndex ? 'bg-white w-6' : 'bg-white/40 w-1'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* RIGHT: CONTENT SECTION (40%) */}
          <div className="w-full md:w-[40%] flex flex-col bg-white overflow-hidden border-l border-gray-100">
            <div className="p-6 md:p-8 overflow-y-auto h-full custom-scrollbar">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                {room.category_name || room.name}
              </h2>
              
              <div className="flex items-center gap-2 text-[10px] font-bold text-[#d99e56] mb-4 uppercase tracking-widest">
                <span>{room.size}</span>
                <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
                <span>Max {room.maxGuests} Guests</span>

                {/* Static View Section vansh change */}
              <>
              {room.room_view?.trim() && (
                <>
                  <span className="text-gray-300">•</span>
                  <span>{room.room_view}</span>
                </>
              )}
            </>
              </div>

              <div className="text-gray-600 mb-6 text-sm leading-relaxed">
                {room.description}
              </div>

    {/* AMENITIES SECTION */}
<div className="space-y-8">
  {(() => {
   return categoryOrder
      .filter(category => Array.isArray(features[category]) && features[category].length > 0)
      .map(category => [category, features[category]])
      // FILTER: Only show the section if it has at least one item
      .filter(([_, items]) => items && items.length > 0)
      .map(([category, items]) => (
        <div key={category} className="last:mb-0">
          {/* Category Header with a subtle side-accent */}
          <div className="flex items-center gap-3 mb-5 border-b border-gray-200 pb-2">
            <h3 className="text-[11px] font-bold text-gray-600 uppercase tracking-[0.2em]">
              {category}
            </h3>
          </div>
          
          {/* Two-Column Grid with Boxy Checkboxes */}
          <div className="grid grid-cols-2 gap-x-10 gap-y-4 px-1">
            {items.map((amenity, index) => (
              <div 
                key={`${category}-${index}`} 
                className="flex items-start gap-3"
              >
                {/* Boxy Checkbox Icon */}
                <div className="flex-shrink-0 mt-0.5 text-[#d99e56]">
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2.5" 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    className="w-4 h-4"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                    <polyline points="9 11 12 14 22 4"></polyline>
                  </svg>
                </div>
                
                <span className="text-[12px] font-medium text-gray-600 leading-tight">
                  {amenity}
                </span>
              </div>
            ))}
          </div>
        </div>
      ));
  })()}
</div>


            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
